import {
  authorizationQrCancelCreate,
  authorizationQrStartCreate,
  type QrLoginStart,
  type QrLoginStartResponse,
  type QrLoginWait,
  type QrLoginWaitApprovedResponse,
  type QrLoginWaitPendingResponse
} from 'src/api/main'

export type QrLoginStartBody = QrLoginStart
export type QrLoginWaitBody = QrLoginWait
export type QrLoginPendingResponse = QrLoginWaitPendingResponse
export type QrLoginApprovedResponse = QrLoginWaitApprovedResponse

export type QrLoginTerminalResponse = {
  status: 'denied' | 'expired' | 'canceled'
}

export type QrLoginWaitResponse =
  | QrLoginPendingResponse
  | QrLoginApprovedResponse
  | QrLoginTerminalResponse

export type QrLoginCancelResponse = {
  status: 'canceled'
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const readString = (value: Record<string, unknown>, key: string): string => {
  const field = value[key]
  if (typeof field !== 'string' || !field) {
    throw new Error(`Invalid QR login response: missing ${key}`)
  }
  return field
}

const readNumber = (value: Record<string, unknown>, key: string): number => {
  const field = value[key]
  if (typeof field !== 'number' || !Number.isFinite(field)) {
    throw new Error(`Invalid QR login response: missing ${key}`)
  }
  return field
}

export const parseQrLoginStartResponse = (
  value: unknown
): QrLoginStartResponse => {
  if (!isRecord(value)) {
    throw new Error('Invalid QR login start response')
  }

  return {
    session_id: readString(value, 'session_id'),
    web_token: readString(value, 'web_token'),
    qr_payload: readString(value, 'qr_payload'),
    expires_in: readNumber(value, 'expires_in'),
    poll_interval: readNumber(value, 'poll_interval'),
    long_poll_timeout: readNumber(value, 'long_poll_timeout')
  }
}

export const parseQrLoginWaitResponse = (
  value: unknown
): QrLoginWaitResponse => {
  if (!isRecord(value)) {
    throw new Error('Invalid QR login wait response')
  }

  const status = readString(value, 'status')

  if (status === 'pending' || status === 'scanned') {
    return {
      status,
      expires_in_remaining: readNumber(value, 'expires_in_remaining')
    }
  }

  if (status === 'approved') {
    const organizationId = value.organization_id

    return {
      status,
      access: readString(value, 'access'),
      refresh: readString(value, 'refresh'),
      organization_id:
        typeof organizationId === 'number' || organizationId === null
          ? organizationId
          : undefined
    }
  }

  if (status === 'denied' || status === 'expired' || status === 'canceled') {
    return { status }
  }

  throw new Error(`Invalid QR login status: ${status}`)
}

export const qrLoginStart = async (body: QrLoginStartBody = {}) => {
  const { data } = await authorizationQrStartCreate({
    body,
    throwOnError: true
  })

  return parseQrLoginStartResponse(data)
}

export const qrLoginCancel = async (body: QrLoginWaitBody) => {
  const { data } = await authorizationQrCancelCreate({
    body,
    throwOnError: true
  })

  const response = parseQrLoginWaitResponse(data)
  if (response.status !== 'canceled') {
    throw new Error(`Invalid QR login cancel status: ${response.status}`)
  }

  return response
}
